import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { PhotoViewer } from '@capacitor-community/photoviewer';
import { Browser } from '@capacitor/browser';
import { AlertController, Platform, ToastController } from '@ionic/angular';
import { select, Store } from '@ngrx/store';

import { OpenOptions } from 'capacitor-email-composer';
import { compact, isNil, isUndefined } from 'lodash-es';
import { combineLatest, map, Observable, of, tap } from 'rxjs';
import { concatMap, filter } from 'rxjs/operators';

import { defaultToastDuration } from '../../../../constants/toast.constants';
import { defaultWalkingFarmRadius } from '../../../../constants/walking-farm.constant';
import { AddressEnum } from '../../../../enums/address';
import { DataProviderIdEnum } from '../../../../enums/data-provider';
import { MainTabBasePathEnum } from '../../../../enums/main-tabs';
import {
  PrimarySubscriptionAccessActionEnum,
  PrimarySubscriptionPlanAccessAliasEnum
} from '../../../../enums/monetization';
import { ProfileSearchEnum } from '../../../../enums/profile-search';
import * as fromNetSheetActions from '../../../../store/actions/net-sheet.actions';
import * as fromPropertyProfileActions from '../../../../store/actions/property-profile.actions';
import * as fromWalkingFarmActions from '../../../../store/actions/walking-farm.actions';
import { PropertyProfile } from '../../../../store/models/property-profile.model';
import * as fromDataProviderSelectors from '../../../../store/selectors/data-provider.selectors';
import * as fromOfficeSelectors from '../../../../store/selectors/office.selectors';
import * as fromOrganizationSelectors from '../../../../store/selectors/organization.selectors';
import * as fromPropertyProfileSelectors from '../../../../store/selectors/property-profile.selectors';
import * as fromTitleRepSelectors from '../../../../store/selectors/title-rep.selectors';
import * as fromUserSelectors from '../../../../store/selectors/user.selectors';
import { GenericComponent } from '../../../components/generic/generic.component';
import { AddressPipe } from '../../../pipes/address.pipe';
import { EmailComposerService } from '../../../services/email-composer.service';
import { MonetizationService } from '../../../services/monetization.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-profile-home',
  standalone: false,
  templateUrl: './home.page.html'
})
export class HomePageComponent extends GenericComponent {
  private activatedRoute = inject(ActivatedRoute);
  private addressPipe = inject(AddressPipe);
  private alertController = inject(AlertController);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private emailComposerService = inject(EmailComposerService);
  private monetizationService = inject(MonetizationService);
  private platform = inject(Platform);
  private store = inject(Store);
  private toastController = inject(ToastController);

  addressType = AddressEnum;
  profileId: string | null;
  profile$: Observable<PropertyProfile>;
  profile: PropertyProfile | null = null;
  hasSchoolsData$: Observable<boolean>;

  orderTitleEnabled$: Observable<boolean>;
  walkingFarmEnabled$: Observable<boolean>;
  calculatorsEnabled$: Observable<boolean>;
  netSheetEnabled$: Observable<boolean>;
  saleInformationEnabled$: Observable<boolean>;
  transactionHistoryEnabled$: Observable<boolean>;
  schoolDistrictInfo$: Observable<boolean>;

  orderTitleOpenOptions: OpenOptions | null = null;

  userEmail: string | null = null;

  isPlatMapViewable = false;
  isPlatMapAvailable = false;

  defaultBackHref: string | undefined;

  constructor() {
    super();

    this.defaultBackHref = this.activatedRoute.snapshot.data['defaultBackHref'] ?? MainTabBasePathEnum.PropertySearch;

    this.profileId = this.activatedRoute.snapshot.paramMap.get('id');
    this.profile$ = of(this.profileId).pipe(
      filter((id) => !isNil(id)),
      concatMap((id) => this.store.pipe(select(fromPropertyProfileSelectors.selectPropertyProfile(id)))),
      filter((propertyProfile) => !isUndefined(propertyProfile)),
      tap((propertyProfile) => {
        this.profile = propertyProfile;
      })
    );
    this.orderTitleEnabled$ = this.store.pipe(select(fromOfficeSelectors.selectOrderTitleEnabled));
    this.walkingFarmEnabled$ = this.store.pipe(select(fromOfficeSelectors.selectWalkingFarmEnabled));
    this.calculatorsEnabled$ = this.store.pipe(select(fromOfficeSelectors.selectCalculatorsEnabled));
    this.netSheetEnabled$ = this.store.pipe(select(fromOfficeSelectors.selectNetSheetsEnabled));
    this.saleInformationEnabled$ = this.store.pipe(select(fromOrganizationSelectors.selectSaleInformationEnabled));
    this.transactionHistoryEnabled$ = this.store.pipe(
      select(fromOrganizationSelectors.selectTransactionHistoryEnabled)
    );
    this.schoolDistrictInfo$ = this.store.pipe(select(fromOfficeSelectors.selectSchoolDistrictInfo));

    this.hasSchoolsData$ = of(this.profileId).pipe(
      filter((id) => !isNil(id)),
      concatMap((id) => this.store.pipe(select(fromPropertyProfileSelectors.selectPropertyProfileHasSchools(id)))),
      filter((hasSchoolsData) => !isUndefined(hasSchoolsData))
    );
  }

  ionViewDidEnter() {
    this.addUniqueSubscription(
      'isPlatMapAvailable',
      of(this.profileId)
        .pipe(
          filter((id) => !isNil(id)),
          concatMap((id) =>
            this.store.pipe(select(fromPropertyProfileSelectors.selectPropertyProfileIsPlatMapAvailable(id)))
          )
        )
        .subscribe((isAvailable) => {
          this.isPlatMapAvailable = isAvailable;
          this.changeDetectorRef.detectChanges();
        })
    );

    this.addUniqueSubscription(
      'isPlatMapViewable',
      this.store
        .pipe(
          select(fromDataProviderSelectors.selectId),
          map((providerId) => providerId === DataProviderIdEnum.BlackKnight)
        )
        .subscribe((isViewable) => {
          this.isPlatMapViewable = isViewable;
          this.changeDetectorRef.detectChanges();
        })
    );

    this.addUniqueSubscription(
      'selectOrderTitleEmail',
      combineLatest([
        this.store.pipe(select(fromOfficeSelectors.selectOrderTitleEnabled)),
        this.store.pipe(select(fromOfficeSelectors.selectOrderTitleEmail)),
        this.store.pipe(select(fromUserSelectors.selectDetails)),
        this.store.pipe(select(fromOrganizationSelectors.selectApplicationName)),
        this.store.pipe(select(fromTitleRepSelectors.selectOrderTitleCCEnabled)),
        this.store.pipe(select(fromTitleRepSelectors.selectEmail))
      ]).subscribe(
        ([orderTitleEnabled, orderTitleEmail, userDetails, applicationName, orderTitleCCEnabled, titleRepEmail]) => {
          if (orderTitleEnabled) {
            this.orderTitleOpenOptions = {
              body: compact([
                'I would like to initiate an order.\n',
                `APN: ${this.profile?.APN}`,
                compact([this.profile?.SiteAddress, this.profile?.SiteUnit]).join(' '),
                compact([this.profile?.SiteCity, [this.profile?.SiteState, this.profile?.SiteZip].join(' ')]).join(
                  ', '
                ),
                '\n',
                `${userDetails.firstName} ${userDetails.lastName}`,
                userDetails.company,
                userDetails.phone,
                userDetails.email
              ]).join('\n'),
              cc: orderTitleCCEnabled && titleRepEmail ? [titleRepEmail] : [],
              isHtml: false,
              subject: `${applicationName ?? 'SmartDirect&#174; Mobile'}: Order Title for ${this.profile?.SiteAddress ?? 'property'}`,
              to: [orderTitleEmail || '']
            };
            this.userEmail = userDetails.email;
          } else {
            this.orderTitleOpenOptions = null;
            this.userEmail = null;
          }
        }
      )
    );
  }

  async orderTitle() {
    if (this.orderTitleOpenOptions) {
      await this.emailComposerService.composeEmail(this.orderTitleOpenOptions);
    }
  }

  async fastFarm() {
    if (this.profile) {
      const hasAccess = await this.monetizationService.accessFeature(
        [
          PrimarySubscriptionPlanAccessAliasEnum.A,
          PrimarySubscriptionPlanAccessAliasEnum.B,
          PrimarySubscriptionPlanAccessAliasEnum.C
        ],
        { accessAction: PrimarySubscriptionAccessActionEnum.CreateFarm }
      );
      if (hasAccess) {
        if (this.profile.latitude && this.profile.longitude) {
          this.store.dispatch(
            fromWalkingFarmActions.newConfiguredFarm({
              payload: {
                searchType:
                  this.defaultBackHref === MainTabBasePathEnum.PropertySearch
                    ? ProfileSearchEnum.PropertySearchWalkingFarm
                    : ProfileSearchEnum.WalkingFarm,
                shapeCoordinates: {
                  latitude: parseFloat(this.profile.latitude),
                  longitude: parseFloat(this.profile.longitude),
                  radius: defaultWalkingFarmRadius
                }
              }
            })
          );
        } else {
          const alert = await this.alertController.create({
            buttons: [
              {
                role: 'cancel',
                text: 'Ok'
              }
            ],
            header: 'Oops',
            message: 'No coordinates available for this property profile in order to create a farm around it.'
          });
          await alert.present();
        }
      }
    }
  }

  async getDirections() {
    await this.platform.ready();
    const isIOS = this.platform.is('ios');
    const address = encodeURIComponent(
      compact([
        this.profile?.SiteUnit,
        this.profile?.SiteAddress,
        this.profile?.SiteCity,
        this.profile?.SiteState,
        this.profile?.SiteZip,
        'US'
      ]).join(', ')
    );
    await Browser.open({
      url: isIOS
        ? `http://maps.apple.com/?daddr=${address}`
        : `https://www.google.com/maps/dir/?api=1&destination=${address}`,
      windowName: '_blank'
    });
  }

  async getPlatMap() {
    if (this.isPlatMapViewable && this.isPlatMapAvailable) {
      await PhotoViewer.show({
        images: [
          {
            title: [this.profile?.SiteAddress, this.profile?.SiteCity].join(' '),
            url: `data:image/${this.profile?.PlatMapEncoding?.toLowerCase()};base64,${this.profile?.PlatMapData}`
          }
        ],
        mode: 'one',
        options: {
          backgroundcolor: 'white',
          divid: 'photo-viewer-container',
          share: false
        }
      });
    } else if (!this.isPlatMapViewable) {
      const alert = await this.alertController.create({
        buttons: [
          {
            role: 'cancel',
            text: 'Cancel'
          },
          {
            handler: async (data) => {
              if (
                String(data.email)
                  .toLowerCase()
                  .match(
                    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                  )
              ) {
                this.store.dispatch(
                  fromPropertyProfileActions.emailPlatMap({
                    payload: {
                      address: this.profile?.SiteAddress || '',
                      deliveryEmail: data.email,
                      zip: this.profile?.SiteZip || ''
                    }
                  })
                );
                return true;
              } else {
                const toast = await this.toastController.create({
                  duration: defaultToastDuration,
                  message: 'Invalid email address',
                  position: 'top'
                });
                await toast.present();
                return false;
              }
            },
            text: 'Send'
          }
        ],
        header: 'Email Plat Map',
        inputs: [
          {
            label: 'Email Address',
            name: 'email',
            placeholder: 'email@example.com',
            type: 'email',
            value: this.userEmail
          }
        ],
        message: `Your plat map will be delivered via email to the following address:`
      });

      await alert.present();
    }
  }

  exportReport() {
    if (this.profile) {
      this.store.dispatch(
        fromPropertyProfileActions.exportReportPdf({
          payload: { address: this.addressPipe.transform(this.profile), reportId: this.profileId || '' }
        })
      );
    }
  }

  prefillNetSheet() {
    this.store.dispatch(
      fromNetSheetActions.loadPropertyProfilePrefillInformation({ payload: { id: this.profileId || '' } })
    );
  }
}
